// Account API — read + update the current trainer's own profile/password, plus
// the Wave 6 email-verification handshake (send link → confirm token) and the
// Wave 7 PUBLIC marketing profile (bio/handle/public toggle + asset galleries).
import { http } from "./http";
import type {
  AccountUpdate,
  AssetUpdate,
  EmailVerifyConfirmResponse,
  EmailVerifySendResponse,
  OkResponse,
  Trainer,
  TrainerAsset,
  TrainerAssetKind,
  TrainerProfile,
  TrainerProfileUpdate,
} from "./types";

export const accountApi = {
  get: (signal?: AbortSignal) =>
    http.get<Trainer>("/account", undefined, signal),
  update: (body: AccountUpdate) => http.patch<Trainer>("/account", body),
  /**
   * Mint + email a single-use verification link to the trainer's on-file email.
   * Requires an email already set (400 otherwise). Never returns the token.
   */
  sendEmailVerification: () =>
    http.post<EmailVerifySendResponse>("/account/email/verify/send"),
  /**
   * Consume a verification token (the SPA verify-email landing route calls this)
   * and mark the trainer's email verified. 400 on invalid/expired/used token.
   */
  confirmEmailVerification: (token: string) =>
    http.post<EmailVerifyConfirmResponse>(
      "/account/email/verify/confirm",
      undefined,
      { token },
    ),

  // --- Wave 7: trainer identity & public marketing profile -----------------

  /** The full public-profile view (fields + the three asset galleries). */
  getProfile: (signal?: AbortSignal) =>
    http.get<TrainerProfile>("/account/profile", undefined, signal),
  /**
   * Update the PUBLIC profile (bio/background/handle/phone/public_enabled).
   * 409 ApiError if the handle is taken, 422 if malformed or reserved.
   */
  updateProfile: (body: TrainerProfileUpdate) =>
    http.patch<TrainerProfile>("/account/profile", body),
  /**
   * Upload (and set) the public AVATAR. Replaces any previous avatar
   * server-side (single-avatar invariant). 413 too large / 422 invalid image.
   */
  uploadAvatar: (file: File, caption?: string) => {
    const form = new FormData();
    form.append("file", file);
    if (caption) form.append("caption", caption);
    return http.upload<TrainerAsset>("/account/avatar", form);
  },
  /** Append one CERTIFICATE image to the gallery (+ optional caption). */
  uploadCertificate: (file: File, caption?: string) => {
    const form = new FormData();
    form.append("file", file);
    if (caption) form.append("caption", caption);
    return http.upload<TrainerAsset>("/account/certificates", form);
  },
  /** Append one ACHIEVEMENT image to the gallery (+ optional caption). */
  uploadAchievement: (file: File, caption?: string) => {
    const form = new FormData();
    form.append("file", file);
    if (caption) form.append("caption", caption);
    return http.upload<TrainerAsset>("/account/achievements", form);
  },
  /** Edit an owned asset's caption / order (404 on a foreign asset). */
  updateAsset: (assetId: number, body: AssetUpdate) =>
    http.patch<TrainerAsset>(`/account/assets/${assetId}`, body),
  /** Delete an owned asset + its public file (404 on a foreign asset). */
  deleteAsset: (assetId: number) =>
    http.del<OkResponse>(`/account/assets/${assetId}`),
  /** Reorder a kind's gallery (owned + matching-kind ids only). */
  reorderAssets: (
    kind: Extract<TrainerAssetKind, "certificate" | "achievement">,
    orderedIds: number[],
  ) =>
    http.post<{ ok: boolean; updated: number }>("/account/assets/reorder", {
      kind,
      ordered_ids: orderedIds,
    }),
};
